import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdAttachFile, MdCheckCircle, MdClose, MdGroups, MdMail, MdSchedule, MdSearch, MdSend, MdShield, MdWarningAmber } from 'react-icons/md';
import { getData, postData, postFileUpload } from '../../../Webservice/Webservice';
import { API_URL } from '../../../Utils/url';
import { FONT, SpamMeter, apiPost, authToken } from './emailShared';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};
const quillFormats = ['header', 'bold', 'italic', 'underline', 'color', 'list', 'bullet', 'align', 'link'];

const MAX_ATTACH_MB = 5;
const EMPTY_FILTERS = { promotionType: 'email', city: [], gender: [], status: [], customerType: [] };

const Section = ({ n, title, hint, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, color: '#0f172a', lineHeight: 1.2 }}>{n}. {title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{hint}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

const MultiSelect = ({ label, value, onChange, options, render }) => (
  <TextField select fullWidth size="small" label={label} value={value} SelectProps={{ multiple: true, renderValue: render || ((sel) => sel.join(', ')) }} onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}>
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        <Checkbox checked={value.includes(o.value)} size="small" />
        <ListItemText primary={o.label} />
      </MenuItem>
    ))}
  </TextField>
);

function EmailForm({ onBack, onCreated, source = null }) {
  const isCopy = !!source;
  const [name, setName] = useState(source ? `${source.name} (copy)` : '');
  // a copy starts as a draft so nothing is sent by accident before it has been reviewed
  const [mode, setMode] = useState(source ? 'draft' : 'now'); // now | schedule | draft
  const [scheduledAt, setScheduledAt] = useState('');
  const [subject, setSubject] = useState(source?.subject || '');
  const [content, setContent] = useState(source?.content || '');
  const [trackOpens, setTrackOpens] = useState(source ? source.trackOpens !== false : true);
  const [attachments, setAttachments] = useState([]);
  const [keptFiles, setKeptFiles] = useState(source?.attachments || []); // files carried over from the original

  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const [report, setReport] = useState(null);
  const [checking, setChecking] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [riskDialog, setRiskDialog] = useState(false);
  const checkSeq = useRef(0);
  const firstLoad = useRef(false);

  // ---------- audience ----------
  const fetchCustomers = async (f) => {
    setLoadingCustomers(true);
    try {
      const res = await postData('campaign/customer/list', f, authToken(), false);
      const unique = new Map();
      (res?.customers || []).forEach((c) => {
        const key = (c.email || '').trim().toLowerCase();
        if (key && !unique.has(key)) unique.set(key, c);
      });
      const list = Array.from(unique.values());
      setCustomers(list);
      // a copy keeps the original recipients that are still eligible; otherwise everyone listed
      const original = !firstLoad.current && source?.customerIds ? new Set(source.customerIds.map(String)) : null;
      firstLoad.current = true;
      setSelected(original ? list.filter((c) => original.has(String(c._id))).map((c) => c._id) : list.map((c) => c._id));
    } catch (e) {
      setCustomers([]);
      setSelected([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    getData('campaign/city/list', null, authToken(), false).then((r) => setCities(r?.cities || [])).catch(() => setCities([]));
    fetchCustomers(EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (key, value) => setFilters((p) => ({ ...p, [key]: value }));
  const filteredPicker = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return q ? customers.filter((c) => `${c.name || ''} ${c.email || ''}`.toLowerCase().includes(q)) : customers;
  }, [customers, pickerSearch]);

  // ---------- live spam check ----------
  useEffect(() => {
    if (!subject.trim() && !content.replace(/<[^>]*>/g, '').trim()) { setReport(null); return undefined; }
    const seq = ++checkSeq.current;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await apiPost('campaign/email/spam-check', { subject, content });
        if (seq === checkSeq.current) setReport(r);
      } catch (e) { /* the check is advisory */ }
      finally { if (seq === checkSeq.current) setChecking(false); }
    }, 700);
    return () => clearTimeout(t);
  }, [subject, content]);

  // ---------- attachments ----------
  const addFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const tooBig = files.filter((f) => f.size > MAX_ATTACH_MB * 1024 * 1024);
    if (tooBig.length) toast.error(`${tooBig.map((f) => f.name).join(', ')} is over ${MAX_ATTACH_MB} MB. Large attachments make an email far more likely to be flagged — share a link instead.`);
    setAttachments((prev) => [...prev, ...files.filter((f) => f.size <= MAX_ATTACH_MB * 1024 * 1024)].slice(0, Math.max(0, 5 - keptFiles.length)));
    e.target.value = '';
  };

  // ---------- test email ----------
  const sendTest = async () => {
    if (!subject.trim() || !content.trim()) return toast.error('Add a subject and content first.');
    setTesting(true);
    try {
      const r = await apiPost('campaign/email/test', { to: testTo, subject, content });
      toast.success(r.message);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTesting(false);
    }
  };

  // ---------- submit ----------
  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = 'Give the campaign a name';
    if (!subject.trim()) next.subject = 'The subject is required';
    if (!content.replace(/<[^>]*>/g, '').trim()) next.content = 'The email content cannot be empty';
    if (mode === 'schedule') {
      if (!scheduledAt) next.scheduledAt = 'Choose when to send';
      else if (new Date(scheduledAt).getTime() < Date.now()) next.scheduledAt = 'That time has already passed';
    }
    if (mode !== 'draft' && !selected.length) next.audience = 'Select at least one customer';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (acknowledgeRisk = false) => {
    if (!validate()) return;
    if (mode !== 'draft' && report && report.score >= report.blockScore && !acknowledgeRisk) {
      setRiskDialog(true);
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('name', name.trim());
      form.append('subject', subject.trim());
      form.append('content', content);
      form.append('mode', mode);
      form.append('trackOpens', String(trackOpens));
      // the address this panel reaches the API at, so the open / unsubscribe links in the email point to the right place
      form.append('apiBaseUrl', API_URL.replace(/\/api\/?$/, ''));
      form.append('customerIds', JSON.stringify(selected));
      if (mode === 'schedule') form.append('scheduledAt', new Date(scheduledAt).toISOString());
      if (acknowledgeRisk) form.append('acknowledgeRisk', 'true');
      if (keptFiles.length) form.append('existingAttachments', JSON.stringify(keptFiles));
      attachments.forEach((f) => form.append('attachments', f));
      await postFileUpload('campaign/email/create', form, authToken(), true);
      onCreated();
    } catch (e) {
      /* the shared service already showed the error */
    } finally {
      setSaving(false);
      setRiskDialog(false);
    }
  };

  const actionLabel = mode === 'now' ? 'Create & start sending' : mode === 'schedule' ? 'Schedule campaign' : 'Save draft';
  const ActionIcon = mode === 'schedule' ? MdSchedule : mode === 'draft' ? MdMail : MdSend;
  const errorIssues = (report?.issues || []).filter((i) => i.severity === 'error');
  const otherIssues = (report?.issues || []).filter((i) => i.severity !== 'error');

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1250, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton onClick={onBack} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>{isCopy ? 'Duplicate Campaign' : 'New Email Campaign'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{isCopy ? 'Everything is copied from the original. Change what you need, then save or send.' : 'Choose who gets it, write the message, check it, then send or schedule.'}</Typography>
          </Box>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Section n={1} title="Campaign details" hint="A name for your own reference and when it should go out" accent="#6366f1" icon={MdSend}>
                <TextField fullWidth size="small" label="Campaign name" required value={name} onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }} error={!!errors.name} helperText={errors.name || 'Only you see this'} sx={{ mb: 2 }} />
                <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)} sx={{ gap: 1 }}>
                  {[
                    ['now', 'Send now', 'Starts as soon as you create it'],
                    ['schedule', 'Schedule', 'Pick a date and time'],
                    ['draft', 'Save as draft', 'Review and start later'],
                  ].map(([v, label, sub]) => (
                    <Box key={v} onClick={() => setMode(v)} sx={{ flex: '1 1 180px', cursor: 'pointer', p: 1.25, borderRadius: '12px', border: `1.5px solid ${mode === v ? 'rgb(255 190 0)' : '#e2e8f0'}`, background: mode === v ? 'rgb(255 209 2 / 12%)' : '#fff' }}>
                      <FormControlLabel value={v} control={<Radio size="small" sx={{ color: 'rgb(255 190 0)', '&.Mui-checked': { color: 'rgb(255 190 0)' } }} />} label={<Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5 }}>{label}</Typography>} sx={{ m: 0 }} />
                      <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b', ml: 3.5 }}>{sub}</Typography>
                    </Box>
                  ))}
                </RadioGroup>
                {mode === 'schedule' && (
                  <TextField fullWidth size="small" type="datetime-local" label="Send at" required InputLabelProps={{ shrink: true }} inputProps={{ min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) }} value={scheduledAt} onChange={(e) => { setScheduledAt(e.target.value); setErrors((p) => ({ ...p, scheduledAt: undefined })); }} error={!!errors.scheduledAt} helperText={errors.scheduledAt} sx={{ mt: 2 }} />
                )}
              </Section>

              <Section n={2} title="Audience" hint="Only customers who agreed to promotional emails are listed" accent="#0ea5e9" icon={MdGroups}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MultiSelect label="City" value={filters.city} onChange={(v) => setFilter('city', v)} options={cities.map((c) => ({ value: c, label: c }))} render={(sel) => (sel.length === cities.length && cities.length ? 'All cities' : sel.join(', '))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MultiSelect label="Gender" value={filters.gender} onChange={(v) => setFilter('gender', v)} options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MultiSelect label="Customer type" value={filters.customerType} onChange={(v) => setFilter('customerType', v)} options={[{ value: 'customer', label: 'Customer (B2C)' }, { value: 'business', label: 'Business (B2B)' }]} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MultiSelect label="Account status" value={filters.status} onChange={(v) => setFilter('status', v)} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  <Button onClick={() => fetchCustomers(filters)} disabled={loadingCustomers} variant="outlined" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', color: '#0f172a', borderColor: '#cbd5e1' }}>
                    {loadingCustomers ? 'Loading…' : 'Apply filters'}
                  </Button>
                  <Button onClick={() => setPickerOpen(true)} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#e0f2fe', color: '#075985', px: 2 }}>
                    {selected.length} of {customers.length} customers selected · review
                  </Button>
                </Box>
                {errors.audience && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 1 }}>{errors.audience}</Typography>}
              </Section>

              <Section n={3} title="Message" hint="Write like a person, not a banner. Use {{firstName}} to greet each customer by name" accent="#10b981" icon={MdMail}>
                <TextField fullWidth size="small" label="Subject" required value={subject} onChange={(e) => { setSubject(e.target.value); setErrors((p) => ({ ...p, subject: undefined })); }} error={!!errors.subject} helperText={errors.subject || `${subject.length} characters · 20–60 works best`} sx={{ mb: 2 }} />
                <Box sx={{ '& .ql-toolbar': { borderRadius: '10px 10px 0 0', borderColor: errors.content ? '#ef4444' : '#e2e8f0', background: '#f8fafc' }, '& .ql-container': { borderRadius: '0 0 10px 10px', borderColor: errors.content ? '#ef4444' : '#e2e8f0', fontFamily: FONT, fontSize: 13.5 }, '& .ql-editor': { minHeight: 260 } }}>
                  <ReactQuill value={content} onChange={(v) => { setContent(v); setErrors((p) => ({ ...p, content: undefined })); }} modules={quillModules} formats={quillFormats} theme="snow" placeholder="Hi {{firstName}}, …" />
                </Box>
                {errors.content && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 0.75 }}>{errors.content}</Typography>}
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mt: 1 }}>An unsubscribe link and your company details are added to the bottom of every email automatically.</Typography>

                <Box sx={{ mt: 2.5 }}>
                  <Button component="label" startIcon={<MdAttachFile />} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', background: '#f1f5f9', color: '#0f172a', px: 2 }}>
                    Add attachment
                    <input type="file" multiple hidden onChange={addFiles} />
                  </Button>
                  <Typography component="span" sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', ml: 1.5 }}>Up to 5 files, {MAX_ATTACH_MB} MB each. Links work better than attachments for deliverability.</Typography>
                  <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {keptFiles.map((u) => (
                      <Box key={u} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: '10px', background: '#eef2ff', border: '1px solid #c7d2fe' }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600 }}>{u.split(/[\\/]/).pop()}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#6366f1' }}>from original</Typography>
                        <IconButton size="small" onClick={() => setKeptFiles((p) => p.filter((x) => x !== u))}><MdClose size={14} /></IconButton>
                      </Box>
                    ))}
                    {attachments.map((f, i) => (
                      <Box key={`${f.name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600 }}>{f.name}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#94a3b8' }}>{(f.size / 1024).toFixed(0)} KB</Typography>
                        <IconButton size="small" onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))}><MdClose size={14} /></IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Section>
            </Box>
          </Grid>

          {/* Deliverability panel */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: 'sticky' }, top: 16, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <MdShield size={20} color="#6366f1" />
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}>Spam check</Typography>
                  {checking && <CircularProgress size={14} />}
                </Box>
                {!report ? (
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>Start writing and your email is checked as you type for the things that send mail to the spam folder.</Typography>
                ) : (
                  <>
                    <SpamMeter report={report} loading={checking} />
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {report.issues.length === 0 && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: '#166534' }}><MdCheckCircle /><Typography sx={{ fontFamily: FONT, fontSize: 12 }}>No problems found.</Typography></Box>
                      )}
                      {[...errorIssues, ...otherIssues].map((i, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <MdWarningAmber size={17} style={{ flexShrink: 0, marginTop: 2, color: i.severity === 'error' ? '#dc2626' : i.severity === 'warning' ? '#f59e0b' : '#94a3b8' }} />
                          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#334155', lineHeight: 1.45 }}>{i.message}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Paper>

              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 0.5 }}>Send yourself a test</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mb: 1.5 }}>Check how it looks and whether it reaches the inbox before the real send.</Typography>
                <TextField fullWidth size="small" type="email" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end"><Button size="small" onClick={sendTest} disabled={testing || !testTo} sx={{ textTransform: 'none', fontWeight: 800 }}>{testing ? 'Sending…' : 'Send test'}</Button></InputAdornment> }} />
                <FormControlLabel sx={{ mt: 1.5, ml: 0 }} control={<Switch checked={trackOpens} onChange={(e) => setTrackOpens(e.target.checked)} size="small" />} label={<Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>Track opens</Typography>} />
                <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#94a3b8', ml: 0.5 }}>Adds a tiny invisible image; some email apps block it, so counts are a minimum.</Typography>
              </Paper>

              <Button onClick={() => submit(false)} disabled={saving} variant="contained" startIcon={saving ? <CircularProgress size={18} sx={{ color: 'black' }} /> : <ActionIcon />} sx={{ fontFamily: FONT, textTransform: 'none', background: 'rgb(255 209 2)', color: 'black', py: 1.4, borderRadius: '12px', fontWeight: 800, fontSize: 13.5, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>
                {saving ? 'Working…' : actionLabel}
              </Button>
              <Button onClick={onBack} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* review recipients */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Recipients ({selected.length} of {customers.length})</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" placeholder="Search name or email" value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} InputProps={{ startAdornment: <MdSearch style={{ marginRight: 8, color: '#94a3b8' }} /> }} sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => setSelected(customers.map((c) => c._id))} sx={{ textTransform: 'none', fontWeight: 700 }}>Select all</Button>
            <Button size="small" onClick={() => setSelected([])} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>
          </Box>
          <Box sx={{ maxHeight: 380, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
            {filteredPicker.length === 0 ? (
              <Typography sx={{ p: 2, fontFamily: FONT, color: '#64748b' }}>No customers match.</Typography>
            ) : (
              filteredPicker.map((c) => (
                <Box key={c._id} onClick={() => setSelected((p) => (p.includes(c._id) ? p.filter((x) => x !== c._id) : [...p, c._id]))} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', '&:hover': { background: '#f8fafc' } }}>
                  <Checkbox checked={selected.includes(c._id)} size="small" disableRipple />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 12.5 }}>{c.name || 'Customer'}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{c.email}</Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPickerOpen(false)} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', '&:hover': { background: 'rgb(255 190 0)' } }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* risky content confirmation */}
      <Dialog open={riskDialog} onClose={() => !saving && setRiskDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>This email may land in spam</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 1.5 }}>Spam risk {report?.score}/100. Sending anyway can hurt your sender reputation, which affects <b>all</b> your emails, including order mails.</Alert>
          {errorIssues.slice(0, 4).map((i, idx) => <Typography key={idx} sx={{ fontFamily: FONT, fontSize: 12, color: '#334155', mb: 0.5 }}>• {i.message}</Typography>)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setRiskDialog(false)} sx={{ textTransform: 'none', fontWeight: 800, color: '#0f172a' }}>Go back and fix</Button>
          <Button onClick={() => submit(true)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Send anyway</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailForm;
